import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { LogOut, Menu, X, ChevronDown, Search as SearchIcon, Command, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FullSpinner } from "@/components/States";
import { api } from "@/lib/api";
import { Icon } from "./adminIcons";
import CommandPalette from "./CommandPalette";

export function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login"); return; }
    if (!user.is_admin) { nav("/app/dashboard"); }
  }, [user, loading, nav]);
  if (loading) return <div className="min-h-screen bg-[#1A090D]"><FullSpinner label="Loading admin…" /></div>;
  if (!user || !user.is_admin) return null;
  return children;
}

const STATE_BANNER = {
  maintenance: { text: "Maintenance mode is ON — the member app is paused for non-admins.", cls: "bg-amber-600/90" },
  read_only: { text: "Read-only mode is ON — members cannot make changes.", cls: "bg-sky-600/90" },
  lockdown: { text: "EMERGENCY LOCKDOWN is ON — the member app is fully blocked.", cls: "bg-rose-700/95" },
};

export function AdminLayout() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [platform, setPlatform] = useState("operational");

  useEffect(() => {
    api.get("/admin/registry").then(({ data }) => setGroups(data.groups)).catch(() => setGroups([]));
    api.get("/platform-status").then(({ data }) => setPlatform(data.mode)).catch(() => {});
  }, [loc.pathname]);

  const toggle = (g) => setCollapsed((c) => ({ ...c, [g]: !c[g] }));

  const Side = () => (
    <nav className="space-y-1.5 pb-8">
      {(groups || []).map((grp) => {
        const isCol = collapsed[grp.group];
        const items = grp.features.filter((f) => f.available);
        if (items.length === 0) return null;
        return (
          <div key={grp.group}>
            <button onClick={() => toggle(grp.group)} data-testid={`admin-group-${grp.group.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#C59B27]/80 hover:text-[#C59B27]">
              <Icon name={grp.icon} className="w-3.5 h-3.5" />
              <span className="flex-1 text-left">{grp.group}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCol ? "-rotate-90" : ""}`} />
            </button>
            {!isCol && (
              <div className="space-y-0.5 mb-1">
                {items.map((f) => {
                  const active = loc.pathname === f.route;
                  return (
                    <Link key={f.key} to={f.route} onClick={() => setOpen(false)} title={f.desc}
                      data-testid={`admin-nav-${f.key}`}
                      className={`flex items-center gap-3 pl-4 pr-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${active ? "bg-[#781826] text-[#FAF5F0]" : "text-[#D8B4B9] hover:bg-[#2A0F14]"}`}>
                      <Icon name={f.icon} className="w-4 h-4 shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  const banner = STATE_BANNER[platform];

  return (
    <div className="min-h-screen bg-[#1A090D] text-[#FAF5F0]" data-testid="admin-layout">
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <header className="sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-[#781826]/40 bg-[#1A090D]/95 backdrop-blur">
        <div className="flex items-center gap-3">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-menu-btn"><Menu className="w-5 h-5" /></button>
          <span className="font-serif font-bold text-xl">Mana<span className="text-[#C59B27]">Jodi</span> <span className="text-xs text-[#C59B27] font-sans">Control Center</span></span>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setPaletteOpen(true)} data-testid="admin-global-search"
            className="hidden sm:flex items-center gap-2 text-xs text-[#D8B4B9] bg-[#2A0F14] border border-[#781826]/50 rounded-lg px-3 py-1.5 hover:border-[#C59B27]/50">
            <SearchIcon className="w-3.5 h-3.5" /> Search… <span className="flex items-center gap-0.5 text-[#8A6D71]"><Command className="w-3 h-3" />K</span>
          </button>
          <span className="hidden md:inline text-xs text-[#8A6D71]">{user?.email}</span>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="text-[#D8B4B9] hover:text-white" data-testid="admin-logout"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>
      {banner && (
        <div className={`${banner.cls} text-white text-xs font-medium px-4 py-1.5 flex items-center gap-2`} data-testid="platform-state-banner">
          <AlertTriangle className="w-3.5 h-3.5" /> {banner.text}
          <Link to="/admin/platform-control" className="underline ml-auto">Manage</Link>
        </div>
      )}
      <div className="flex">
        <aside className="hidden lg:block w-64 shrink-0 border-r border-[#781826]/40 p-3 overflow-y-auto h-[calc(100vh-57px)] sticky top-[57px]">
          {groups === null ? <p className="text-xs text-[#8A6D71] p-2">Loading modules…</p> : <Side />}
        </aside>
        {open && (
          <div className="fixed inset-0 z-50 lg:hidden"><div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#1A090D] p-3 overflow-y-auto"><button onClick={() => setOpen(false)} className="mb-3"><X className="w-5 h-5" /></button><Side /></div>
          </div>
        )}
        <main className="flex-1 min-w-0 p-4 sm:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
